import React, { Component } from 'react';
import styled from '@emotion/styled';

import PropTypes from 'prop-types';
import Field from 'redux-form/lib/Field';
import { Link as ReactRouterDOMLink } from 'react-router-dom';
import reduxForm from 'redux-form/lib/reduxForm';

import ButtonWithHoverAndPress from '../../shared/Button/ButtonWithHoverAndPress';
import Captcha from '../Captcha';
import { FieldInputWithRedux } from '../FieldInput';
import { LinkText } from '../../shared/Link/index.tsx';
import ErrorAlert from './components/ErrorAlert';
import events from '../SignupPageContainer/events';

import {
  EXISTING_ACCOUNT_ERROR_MESSAGE,
  EXISTING_EMAIL_ERROR_MESSAGE,
  PASSWORD_ERROR_MESSAGE,
  validate,
} from '../../user';
import styles from '../OnboardingRouter/styles.sass';
import Box from '../../shared/Box';

const INPUT_FIELDS = [
  {
    name: 'name',
    placeholder: 'Full Name',
    type: 'text',
  },
  {
    name: 'email',
    placeholder: 'Email',
    type: 'email',
  },
  {
    name: 'password',
    placeholder: 'Password',
    type: 'password',
  },
];

const FieldsContainer = styled.div`
  max-width: 410px;
  width: 100%;
  margin: auto;
`;

const FieldWrapper = styled.div`
  margin-bottom: 10px;
`;
const CatpchaWrapper = styled.div`
  margin-top: 24px;
`;

const ButtonWrapper = styled.div`
  margin: auto;
  margin-bottom: 14px;
  margin-top: 32px;
  max-width: 300px;
  width: 100%;
`;

// This is currently built only for onboarding
class SignupForm extends Component {
  handleClickRequestResetPassword = evt => {
    const { onClickRequestResetPassword } = this.props;
    onClickRequestResetPassword();
  };

  loginUrl = () => {
    const { feedUrl } = this.props;
    return `/login${feedUrl ? `?feedUrl=${encodeURIComponent(feedUrl)}` : ''}`;
  };

  render() {
    const {
      error,
      handleSubmit,
      submitting,
      invalid,
      isLoading,
      isV3Captcha = false,
    } = this.props;
    const isSubmitDisabled = isLoading || submitting || invalid;
    return (
      <form
        onSubmit={data => {
          if (!isSubmitDisabled) handleSubmit(data);
        }}
        id="SignupForm"
      >
        <FieldsContainer>
          {INPUT_FIELDS.map(field => (
            <FieldWrapper>
              <Field
                name={field.name}
                placeholder={field.placeholder}
                component={FieldInputWithRedux}
                type={field.type}
                key={field.name}
                autofocus={field.name === 'name'}
              />
            </FieldWrapper>
          ))}
          <CatpchaWrapper>
            <Field
              name="captcharesponse"
              isV3={isV3Captcha}
              component={Captcha}
            />
          </CatpchaWrapper>
        </FieldsContainer>
        {error && (
          <Box display="flex" justifyContent="center">
            <Box maxWidth={404} width="100%">
              {(() => {
                switch (error) {
                  case EXISTING_ACCOUNT_ERROR_MESSAGE:
                    return (
                      <ErrorAlert
                        title="Existing account found"
                        renderMessage={
                          <React.Fragment>
                            We found an existing account that uses these
                            credentials. You can{' '}
                            <ReactRouterDOMLink
                              to={this.loginUrl()}
                              onClick={() => events.signUpSignInButtonClicked()}
                            >
                              log in
                            </ReactRouterDOMLink>{' '}
                            ‌or try again with a different email address.
                          </React.Fragment>
                        }
                      />
                    );
                  case EXISTING_EMAIL_ERROR_MESSAGE:
                    return (
                      <ErrorAlert
                        title="This email address is already being used"
                        renderMessage={
                          <React.Fragment>
                            We found an existing account with this email
                            address, but this password doesn’t match. You can
                            try again or{' '}
                            <ReactRouterDOMLink
                              to={this.loginUrl()}
                              onClick={this.handleClickRequestResetPassword}
                            >
                              reset your password
                            </ReactRouterDOMLink>
                            .
                          </React.Fragment>
                        }
                      />
                    );
                  case PASSWORD_ERROR_MESSAGE:
                    return <ErrorAlert title={error} />;
                  default:
                    return (
                      <ErrorAlert
                        title="Unable to create account"
                        renderMessage={
                          <React.Fragment>
                            Sorry, but we were unable to create your account.
                            Please refresh the page and try again, or visit{' '}
                            <LinkText
                              to="https://help.anchor.fm/hc/en-us"
                              isInline
                            >
                              help.anchor.fm
                            </LinkText>{' '}
                            so we can help you troubleshoot.
                          </React.Fragment>
                        }
                      />
                    );
                }
              })()}
            </Box>
          </Box>
        )}
        <ButtonWrapper>
          <ButtonWithHoverAndPress
            colorTheme="primary"
            isDisabled={isSubmitDisabled}
            isProcessing={submitting || isLoading}
            isFullWidth
            size="sm"
            shape="pill"
            text="Sign up"
            type="unelevated"
            typeAttribute="submit"
            isEventBubblingAllowed={!isSubmitDisabled}
            onPress={events.signUpRegisterButtonClicked}
          />
        </ButtonWrapper>
        <div className={styles.terms}>
          By continuing, you agree to our{' '}
          <LinkText
            target="blank"
            onClick={() => events.signUpLegalLinkClicked({ link: 'tos' })}
            isInline
            to="/tos"
            color="gray"
          >
            Terms of Service
          </LinkText>{' '}
          and{' '}
          <LinkText
            target="blank"
            onClick={() => events.signUpLegalLinkClicked({ link: 'privacy' })}
            isInline
            color="gray"
            to="/privacy"
          >
            Privacy Policy
          </LinkText>
          .
        </div>
      </form>
    );
  }
}

SignupForm.propTypes = {
  error: PropTypes.string,
  feedUrl: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  invalid: PropTypes.bool.isRequired,
  onClickRequestResetPassword: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  submitting: PropTypes.bool.isRequired,
};

SignupForm.defaultProps = {
  error: undefined,
  feedUrl: '',
  isLoading: false,
};

export default reduxForm({
  form: 'createAccount',
  validate,
})(SignupForm);
